import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import { Page } from '@/components/layout/Page'
import { SettingsStatus, settingsPrimaryClass } from '@/components/SettingsTabs'
import { api } from '@/lib/api'
import { useSelection } from '@/lib/useSelection'

type EntityStatus = {
  entity_type: string
  /** idle | queued | running | ok | error */
  status: string
  count: number
  /** last successful sync */
  synced_at: string | null
  /** only while running */
  started_at: string | null
  error: string | null
}

type CacheStatus = {
  running: boolean
  next_scheduled_at: string | null
  entities: EntityStatus[]
}

/** Status is polled this often while a sync runs… */
const POLL_MS = 3000
/** …and this often while idle, so a scheduled run or someone else's run shows up. */
const IDLE_POLL_MS = 60_000

// Backend ENTITY_TYPES; the label is the detail pane title.
const LABELS: Record<string, string> = {
  field: 'fields',
  context: 'field contexts',
  user: 'users',
  group: 'groups',
  team: 'teams',
  config: 'config',
  workflow: 'workflows',
}

const BUSY_TITLE = 'A sync is already running'

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')
const clock = (iso: string, seconds = false) =>
  new Date(iso).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}), hourCycle: 'h23',
  })

/** api() throws `Error("<status> <body>")`; pull the status code and the JSON body back out. */
function httpError(e: unknown): { code: number; body: unknown } | null {
  const m = /^(\d{3}) ([\s\S]*)$/.exec(e instanceof Error ? e.message : '')
  if (!m) return null
  let body: unknown = m[2]
  try { body = JSON.parse(m[2]) } catch { /* plain text body */ }
  return { code: Number(m[1]), body }
}

function errorText(e: unknown): string {
  const detail = (httpError(e)?.body as { detail?: unknown } | null)?.detail
  return `Error: ${typeof detail === 'string' ? detail : e instanceof Error ? e.message : String(e)}`
}

/**
 * Compact secondary action that fits a 30 px table row. The ::after layer stretches the hit area
 * over the whole cell (its padding included), so a click next to the button syncs instead of
 * falling through to the row and selecting it. The offsets mirror DataTable's td padding
 * (py-[5px], first:pl-4 / last:pr-4, px-2.5) — keep them in step.
 * Busy state is aria-disabled, not `disabled`: a disabled button lets the click through to the row.
 */
const rowButtonClass =
  'relative inline-flex h-5 after:absolute after:-inset-y-[6px] after:-left-3 after:-right-[17px] after:content-[\'\'] items-center rounded-md border border-line-strong bg-surface-raised px-2 text-[11.5px] leading-none text-foreground transition-colors hover:bg-surface-active aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-surface-raised'

export default function SettingsCache() {
  const [data, setData] = useState<CacheStatus | null>(null)
  const [posting, setPosting] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [sel, setSel] = useSelection()

  const load = useCallback(
    () =>
      api<CacheStatus>('/cache/status')
        .then(setData)
        .catch((e: unknown) => {
          setData((d) => d ?? { running: false, next_scheduled_at: null, entities: [] })
          setMsg(errorText(e))
        }),
    [],
  )

  useEffect(() => {
    void load()
  }, [load])

  const running = data?.running ?? false

  // Poll fast while the backend says a sync runs, slowly while idle; cleared on unmount.
  useEffect(() => {
    const iv = setInterval(() => void load(), running ? POLL_MS : IDLE_POLL_MS)
    return () => clearInterval(iv)
  }, [running, load])

  async function sync(entities?: string[]) {
    setPosting(true)
    setMsg(null)
    try {
      const r = await api<CacheStatus>('/cache/sync', {
        method: 'POST',
        ...(entities ? { body: JSON.stringify({ entities }) } : {}),
      })
      setData(r)
      setMsg(`Sync started: ${entities ? entities.join(', ') : 'all types'}`)
    } catch (e) {
      const h = httpError(e)
      const detail = (h?.body as { detail?: unknown } | null)?.detail
      if (h?.code === 409 && detail && typeof detail === 'object') {
        const types = (detail as { running?: string[] }).running ?? []
        setMsg(`A sync is already running: ${types.length ? types.join(', ') : 'another sync'}`)
        // someone else's run — follow it until it finishes
        setData((d) => (d ? { ...d, running: true } : d))
      } else {
        setMsg(errorText(e))
      }
    } finally {
      setPosting(false)
    }
  }

  const busy = running || posting
  const rows = data?.entities ?? []
  const selected = rows.find((r) => r.entity_type === sel) ?? null
  const latest = rows.map((r) => r.synced_at).filter((s): s is string => !!s).sort().pop()
  const errors = rows.filter((r) => r.status === 'error').length
  const next = data?.next_scheduled_at ?? null

  const columns: Column<EntityStatus>[] = [
    { key: 'entity_type', header: 'Entity', mono: true, width: '16%' },
    { key: 'count', header: 'Cached', mono: true, width: '110px', align: 'right' },
    { key: 'synced_at', header: 'Last synced', mono: true, width: '200px', render: (r) => when(r.synced_at), title: (r) => when(r.synced_at) },
    {
      key: 'status',
      header: 'Status',
      width: '200px',
      title: (r) => (r.started_at ? `${r.status}, started ${when(r.started_at)}` : r.status),
      render: (r) => (
        // status word and its start time on one centred line
        <span className="flex items-center gap-1.5 leading-[18px]">
          <StatusText status={r.status} />
          {r.status === 'running' && r.started_at && (
            <span className="font-mono text-[11.5px] text-text-3">started {clock(r.started_at, true)}</span>
          )}
        </span>
      ),
    },
    { key: 'error', header: 'Error', mono: true, render: (r) => (r.error ? <span className="text-danger">{r.error}</span> : '—'), title: (r) => r.error ?? undefined },
    {
      key: 'sync',
      header: '',
      width: '80px',
      align: 'right',
      title: () => undefined,
      render: (r) => (
        <button
          type="button"
          aria-label={`Sync ${r.entity_type}`}
          className={rowButtonClass}
          aria-disabled={busy}
          title={busy ? BUSY_TITLE : undefined}
          // the row click opens details; the button only syncs (and never selects, even when busy)
          onClick={(e) => {
            e.stopPropagation()
            if (!busy) void sync([r.entity_type])
          }}
        >
          Sync
        </button>
      ),
    },
  ]

  let freshness: ReactNode
  if (next || latest) {
    freshness = (
      <span className="font-mono">
        {next && <>Next scheduled: {clock(next)}</>}
        {next && latest && <span aria-hidden className="mx-1.5">·</span>}
        {latest && <span title={when(latest)}>synced {when(latest)}</span>}
      </span>
    )
  }

  return (
    <Page
      title="Jira cache"
      hint="Pulls fields, contexts, users, groups, teams, config and workflows from Jira so forms can autocomplete. Field options load on demand. Syncs run in the background and on a schedule."
      freshness={freshness}
      actions={(
        <button type="button" className={settingsPrimaryClass} onClick={() => void sync()} disabled={busy}>
          <RefreshCw aria-hidden className={`size-3.5 ${running ? 'animate-spin' : ''}`} />
          Sync all
        </button>
      )}
    >
      <FilterBar>
        <span className="font-mono text-[11px] text-text-3">
          {rows.length} entity types · {errors} with errors{running && ' · sync running, refreshing every 3 s'}
        </span>
        {msg && <span className="ml-auto"><SettingsStatus msg={msg} /></span>}
      </FilterBar>
      <SplitView
        onCloseDetail={() => setSel(null)}
        emptyDetail="Select an entity type to see its sync details."
        detail={selected && (
          <DetailPane
            eyebrow="Cached entity"
            actions={(
              <button
                type="button"
                aria-label={`Sync selected (${selected.entity_type})`}
                aria-disabled={busy}
                title={busy ? BUSY_TITLE : undefined}
                className={`${detailActionClass} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
                onClick={() => { if (!busy) void sync([selected.entity_type]) }}
              >
                Sync
              </button>
            )}
            title={LABELS[selected.entity_type] ?? selected.entity_type} status={<StatusText status={selected.status} />}>
            <KeyValue
              items={[
                ['Entity', <span className="font-mono">{selected.entity_type}</span>],
                ['Cached', <span className="font-mono">{selected.count}</span>],
                ['Last synced', <span className="font-mono">{when(selected.synced_at)}</span>],
                ...(selected.started_at
                  ? [['Started', <span className="font-mono">{when(selected.started_at)}</span>] as [string, ReactNode]]
                  : []),
              ]}
            />
            {selected.error && (
              <pre className="whitespace-pre-wrap break-words rounded-md bg-surface p-2.5 font-mono text-[12px] text-danger">{selected.error}</pre>
            )}
          </DetailPane>
        )}
        list={(
          <DataTable
            label="Cached entity types"
            rows={rows}
            columns={columns}
            rowId={(r) => r.entity_type}
            selectedId={sel}
            onSelect={setSel}
            empty={data === null ? 'Loading…' : 'No cache status — check the Jira token in Settings.'}
          />
        )}
      />
    </Page>
  )
}
